import type { ConfigProviderProps } from 'antd';
import { ConfigProvider, App as AntdApp } from 'antd';
import { themeContext, useThemeContextValue } from './context';
import dayjs from 'dayjs';
import { ProProvider } from '@ant-design/pro-components';
import { useI18nContext } from '../i18n/context';
import { dark, light } from '@/configs/theme/theme';
import { valueTypeMap } from '@/configs/theme/valueTypeMap';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ThemeProviderProps extends ConfigProviderProps {}

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  const values = useContext(ProProvider);
  const value = useThemeContextValue();

  const { isDark } = value;

  const {
    languageItem: { antdPackage, dayjsKey },
  } = useI18nContext();

  const config = useMemo<ConfigProviderProps>(
    () => ({
      theme: {
        cssVar: {
          prefix: '',
        },
        hashed: false,
        ...(isDark ? dark : light),
      },
      locale: antdPackage,
      ...props,
    }),
    [isDark, antdPackage, props]
  );

  const render = useCallback(
    (children: ReactNode) => (
      <themeContext.Provider value={value}>
        <ConfigProvider {...config}>
          <AntdApp>{children}</AntdApp>
        </ConfigProvider>
      </themeContext.Provider>
    ),
    [value, config]
  );

  useLayoutEffect(() => {
    ConfigProvider.config({
      holderRender: render,
    });
  }, [render]);

  useLayoutEffect(() => {
    dayjs.locale(dayjsKey);
  }, [dayjsKey]);

  return (
    <ProProvider.Provider
      value={{
        ...values,
        valueTypeMap,
      }}
    >
      {render(children)}
    </ProProvider.Provider>
  );
}
